import {inject, Injectable} from '@angular/core';
import {ColumnParamsStateService} from "./column-params-state.service";
import {UserPreferencesService} from "../user-preferences.service";
import {ColumnPropertyGroup, RecordTableColumnParams, ColumnsForToggling} from "../../types/records-table.types";
import {
  HideGroupColumnsParams,
  UpdateGroupsInUserPreferencesParams
} from "../../types/table-types/table-group-header.types";
import {UserPreferences} from "../../types/user-preferences.types";

@Injectable({
  providedIn: 'root'
})
export class TableGroupHeaderService {
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);

  public getGroupColor(columnsParams: RecordTableColumnParams): string {
    const group: ColumnPropertyGroup | undefined = columnsParams.columnProperties.GROUP;
    return group ? group.color : 'transparent';
  }

  public toggleGroupVisibility(key: string, columnParams: RecordTableColumnParams): void {
    const groupId = this.getGroupIdFromColumn(columnParams);

    if (!groupId) { return; }

    const groupColumns = this.getNonStickyColumnsByGroupId(groupId, key);

    if (!groupColumns.length) { return; }

    this.hideGroupColumns({
      groupId: groupId,
      columns: groupColumns,
      isGroupHidden: !columnParams.hiddenGroup,
      key: key
    });
  }

  private getGroupIdFromColumn(columnParams: RecordTableColumnParams): string | number | undefined {
    return columnParams.columnProperties.GROUP?.id;
  }

  private getNonStickyColumnsByGroupId(groupId: string | number, key: string): RecordTableColumnParams[] {
    return this.columnParamsStateService.getColumnParamsByKey(key)?.filter(column =>
      column.columnProperties.GROUP?.id === groupId && !column.sticky
    ) || [];
  }

  private hideGroupColumns({ groupId, columns, isGroupHidden, key }: HideGroupColumnsParams): void {
    const columnsForToggling: ColumnsForToggling = { [groupId]: columns };
    this.columnParamsStateService.updateHiddenGroup(key, columnsForToggling);
    this.updateGroupsInUserPreferences({
      groupId: groupId,
      isGroupHidden: isGroupHidden
    });
  }

  private updateGroupsInUserPreferences({ isGroupHidden, groupId }: UpdateGroupsInUserPreferencesParams): void {
    isGroupHidden ?
      this.addHiddenGroupIdToUserPreferences(String(groupId)) :
      this.removeHiddenGroupFromUserPreferences(String(groupId));
  }

  private addHiddenGroupIdToUserPreferences(groupId: string): void {
    const userPreferences: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    const hiddenGroupsIds: Array<string> = userPreferences?.hiddenGroupsKeys ?? [];
    hiddenGroupsIds.push(groupId);
    this.userPreferencesService.saveUserPreferences({ hiddenGroupsKeys: hiddenGroupsIds });
  }

  private removeHiddenGroupFromUserPreferences(groupId: string): void {
    const userPreferences: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    const hiddenGroupsIds: Array<string> | undefined = userPreferences?.hiddenGroupsKeys;
    if (!hiddenGroupsIds || !userPreferences) { return; }

    userPreferences.hiddenGroupsKeys = hiddenGroupsIds.filter((hiddenGroupId: string) => hiddenGroupId !== groupId);
    this.userPreferencesService.saveUserPreferences(userPreferences);
  }
}
